import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  LinearProgress,
  Link,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  FilePresent,
  Description,
  InsertDriveFile,
  Delete,
  Visibility,
  Schedule,
  Done,
  Close,
  HourglassEmpty,
  ExpandMore,
  ExpandLess,
  Email,
  ContentCopy,
  Replay,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { auth } from '../firebase';
import { getApiUrl, readJsonResponse } from '../utils/apiConfig';
import { ConfirmDialog, EmptyState, KpiCard, KpiGrid, LoadingState, PageContainer, TableSkeleton, PageHeader, StatusChip, statusTone, statusLabel } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { displayableIngestWarnings } from '../utils/ingestWarnings';
import { useToast } from '../contexts/ToastContext';
import { CorrectRebateDatesDialog, PricingWindowHold } from '../components/pricing/CorrectRebateDatesDialog';
import { tokens } from '../theme/tokens';
import { formatDate, formatNumber } from '../i18n';

interface UploadResult {
  submissionId: string;
  fileName: string;
  vendor: string;
  region: string;
  category?: string;
  status: string;
  recordsFound: number;
  recordsCreated: number;
  recordsUpdated: number;
  placeholdersCreated: number;
  warnings: string[];
  errors: string[];
}

interface Submission {
  windowHold?: PricingWindowHold;
  /** A vendor's private sheet: its store accepts it, not an admin. */
  privateToOrganization?: boolean;
  id: string;
  fileName: string;
  vendor: string;
  region: string;
  category?: string;
  status: string;
  recordsFound: number;
  recordsCreated: number;
  recordsUpdated: number;
  placeholdersCreated: number;
  errors: string[];
  warnings: string[];
  submitterType: string;
  submitterEmail?: string;
  source?: 'upload' | 'email' | 'admin';
  emailFrom?: string;
  /** Person who posted the message when a group rewrote From. */
  emailOriginalFrom?: string;
  emailSubject?: string;
  createdAt: Date;
  processedAt?: Date;
}

/** One line for the submissions list: how this file got here. */
function arrivalCaption(sub: Submission): string | null {
  if (sub.source === 'email') {
    const who = sub.emailOriginalFrom || sub.emailFrom;
    return who ? `Email from ${who}` : 'Email';
  }
  if (sub.submitterEmail) return `Uploaded by ${sub.submitterEmail}`;
  if (sub.source === 'upload' || sub.source === 'admin') return 'Uploaded in Motzu';
  return null;
}

interface IngestionStats {
  submissions: {
    total: number;
    /** Sheets waiting on the store's own decision (`status: 'pending'`). */
    pending: number;
    /** Sheets in the Pending Approval tab (`adminApprovalStatus: 'pending'`). */
    awaitingAdminApproval?: number;
    processed: number;
    rejected: number;
    duplicate: number;
  };
  records: {
    created: number;
    updated: number;
  };
  products: {
    total: number;
    complete: number;
    placeholder: number;
    withPricing: number;
  };
  pricing: {
    total: number;
    active: number;
    upcoming: number;
    expired: number;
  };
}

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken(true);
};

interface PricingRecord {
  id: string;
  model: string;
  brand: string;
  region: string;
  mapPrice?: number;
  sellingPrice?: number;
  instantRebate?: number;
  tradeCredit?: number;
  effectiveDate?: string;
  endDate?: string;
  status: string;
}

interface EmailIngestionRecord {
  id: string;
  emailId: string;
  from: string;
  subject: string;
  receivedAt: string;
  attachmentsProcessed: number;
  recordsParsed: number;
  recordsSaved: number;
  errors: string[];
  status: 'success' | 'partial' | 'error';
  processedAt: string;
  organizationId?: string;
  submissionIds?: string[];
  retryCount?: number;
  /** Attachments that saved nothing on purpose (already loaded, or no pricing in the file). */
  notes?: string[];
  /** Draft pull request that teaches the parser this document, once an AI read recovered it. */
  parserFix?: { id: string; status: 'queued' | 'opened' | 'out_of_scope' | 'needs_ticket'; prUrl?: string };
  /** Present for ProMaster discontinued-products reports (body-only emails, no submission). */
  discontinuedReport?: {
    rowsProcessed: number;
    productsUpdated: number;
    alreadyDiscontinued: number;
    unmatched: number;
    unmatchedSamples?: string[];
  };
}

/** The message the API put in its error envelope (`{ error: { message } }` or `{ message }`), else the fallback. */
const apiErrorMessage = (body: any, fallback: string): string =>
  (typeof body?.error?.message === 'string' && body.error.message) ||
  (typeof body?.error === 'string' && body.error) ||
  (typeof body?.message === 'string' && body.message) ||
  fallback;

/** Date cell for a submission list; a missing or unparseable value shows a dash, never "Invalid Date". */
const formatSubmittedAt = (value: unknown): string => {
  if (!value) return '—';
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/** API returns nested `{ record: {...} }` from submission records; normalize for the table. */
function flattenSubmissionPricingRecords(raw: unknown[]): PricingRecord[] {
  return (raw || []).map((item: any) => {
    const nested = item?.record || item;
    return {
      id: item?.id || `${nested?.model || 'row'}-${nested?.region || ''}`,
      model: nested?.model || '—',
      brand: nested?.brand || '—',
      region: nested?.region || '—',
      mapPrice: nested?.mapPrice ?? nested?.sellingPrice,
      sellingPrice: nested?.sellingPrice,
      instantRebate: nested?.instantRebate,
      tradeCredit: nested?.tradeCredit,
      effectiveDate: nested?.effectiveDate,
      endDate: nested?.endDate,
      status: item?.approvalStatus || nested?.status || 'active',
    };
  });
}

export const AdminPricingIngestionPage: React.FC = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useUrlTab(['upload', 'pending', 'history', 'email'] as const, 'upload');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [correctingSubmission, setCorrectingSubmission] = useState<Submission | null>(null);
  const [emailDetailsOpen, setEmailDetailsOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailIngestionRecord | null>(null);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const [reprocessAllConfirmOpen, setReprocessAllConfirmOpen] = useState(false);
  const { showToast } = useToast();
  const [addressCopied, setAddressCopied] = useState(false);

  const openEmailDetails = useCallback((record: EmailIngestionRecord) => {
    setSelectedEmail(record);
    setEmailDetailsOpen(true);
  }, []);

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<IngestionStats>({
    queryKey: ['ingestion-stats'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/stats`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const result = await response.json();
      return result.data;
    },
  });

  // Fetch submissions
  const { data: submissions, isLoading: submissionsLoading, refetch: refetchSubmissions } = useQuery<Submission[]>({
    queryKey: ['ingestion-submissions'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/submissions?limit=50`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch submissions');
      const result = await response.json();
      return result.data;
    },
  });

  // Fetch pending submissions
  const { data: pendingSubmissions, refetch: refetchPending } = useQuery<Submission[]>({
    queryKey: ['ingestion-pending'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/pending`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch pending');
      const result = await response.json();
      return result.data;
    },
  });

  // One count for the card and the tab: the server's full count, falling back
  // to the (50-row) list only when the stats have not loaded.
  // A held vendor sheet is listed but has no admin approval for the stats to count.
  const pendingApprovalCount = Math.max(stats?.submissions.awaitingAdminApproval ?? 0, pendingSubmissions?.length ?? 0);

  const uploadWarnings = displayableIngestWarnings(uploadResult?.vendor, uploadResult?.warnings);
  const detailWarnings = displayableIngestWarnings(selectedSubmission?.vendor, selectedSubmission?.warnings);

  // Fetch pricing records for selected submission
  const { data: submissionRecords, isLoading: recordsLoading } = useQuery<PricingRecord[]>({
    queryKey: ['submission-records', selectedSubmission?.id],
    queryFn: async () => {
      if (!selectedSubmission?.id) return [];
      const response = await fetch(
        `${getApiUrl()}/pricing-ingestion/submissions/${selectedSubmission.id}/records?limit=1000`,
        { headers: { Authorization: `Bearer ${await getAuthToken()}` } },
      );
      if (!response.ok) return [];
      const result = await response.json();
      return flattenSubmissionPricingRecords(result.data || []);
    },
    enabled: !!selectedSubmission?.id && detailsDialogOpen,
  });

  // Pricing rows for a clicked email ingestion (may span multiple attachment submissions)
  const { data: emailPricingRecords, isLoading: emailRecordsLoading } = useQuery<PricingRecord[]>({
    queryKey: ['email-ingestion-records', selectedEmail?.id, selectedEmail?.submissionIds],
    queryFn: async () => {
      const ids = selectedEmail?.submissionIds?.filter(Boolean) || [];
      if (ids.length === 0) return [];
      const token = await getAuthToken();
      const batches = await Promise.all(
        ids.map(async (submissionId) => {
          const response = await fetch(
            `${getApiUrl()}/pricing-ingestion/submissions/${submissionId}/records?limit=1000`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (!response.ok) return [] as PricingRecord[];
          const result = await response.json();
          return flattenSubmissionPricingRecords(result.data || []);
        }),
      );
      return batches.flat();
    },
    enabled: !!selectedEmail && emailDetailsOpen,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiUrl()}/pricing-ingestion/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      return response.json();
    },
    onSuccess: (data) => {
      setUploadResult(data.data);
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
    },
    onError: (error: Error) => {
      setUploadError(error.message);
      setUploadResult(null);
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (submissionId: string) => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/submissions/${submissionId}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
          'Content-Type': 'application/json',
        },
      });
      const body = await readJsonResponse(response, 'Failed to approve');
      if (!response.ok) throw new Error(apiErrorMessage(body, 'Failed to approve'));
      return body;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-pending'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      const r = data?.data;
      showToast(
        r && typeof r.recordsCreated === 'number'
          ? `Approved: ${r.recordsCreated} created, ${r.recordsUpdated ?? 0} updated`
          : 'Submission approved',
        'success',
      );
    },
    onError: (error: Error) => {
      showToast(`Approve failed: ${error.message}`, 'error');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ submissionId, reason }: { submissionId: string; reason: string }) => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/submissions/${submissionId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });
      const body = await readJsonResponse(response, 'Failed to reject');
      if (!response.ok) throw new Error(apiErrorMessage(body, 'Failed to reject'));
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-pending'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      showToast('Submission rejected', 'success');
    },
    onError: (error: Error) => {
      showToast(`Reject failed: ${error.message}`, 'error');
    },
  });

  // Clear all data mutation (for re-import)
  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getApiUrl()}/pricing-ingestion/clear-all`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to clear data');
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-pending'] });
      setUploadResult(null);
      setUploadError(null);
      showToast(`Cleared: ${data.deleted?.masterPricing || 0} pricing records, ${data.deleted?.submissions || 0} submissions, ${data.deleted?.placeholders || 0} placeholders`, 'success');
    },
    onError: (error: Error) => {
      showToast(`Failed to clear data: ${error.message}`, 'error');
    },
  });

  // Email ingestion (admin/global scope) — fetch forwarding address
  const { data: emailAddressData, isLoading: emailAddressLoading } = useQuery<{
    forwardingAddress: string;
    scope: string;
  }>({
    queryKey: ['email-map-admin-address'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/address`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch forwarding address');
      return response.json();
    },
  });

  // Email ingestion history — platform-wide, includes emails with no organizationId
  const { data: emailHistory, isLoading: emailHistoryLoading, refetch: refetchEmailHistory } = useQuery<EmailIngestionRecord[]>({
    queryKey: ['email-map-admin-history'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/history?limit=20`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch email ingestion history');
      const result = await response.json();
      return result.history || [];
    },
  });

  // Email ingestion stats — platform-wide
  const { data: emailStats } = useQuery<{
    totalEmailsProcessed: number;
    totalRecordsSaved: number;
    totalErrors: number;
    successRate: string;
  }>({
    queryKey: ['email-map-admin-stats'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/stats`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch email ingestion stats');
      const result = await response.json();
      return result.stats;
    },
  });

  // Manually trigger a check of the inbox (global — not org-scoped)
  const checkEmailNowMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Check failed');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-history'] });
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
    },
  });

  // Reprocess a failed/partial email ingestion (refetches from Gmail and
  // re-runs the pipeline, overwriting the existing history record)
  const reprocessEmailMutation = useMutation({
    mutationFn: async (ingestionId: string) => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/reprocess/${ingestionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error?.message || result?.message || 'Reprocess failed');
      return result;
    },
    onSuccess: (data) => {
      const r = data?.result;
      showToast(
        r?.status === 'success'
          ? `Reprocessed successfully: ${r.recordsSaved} records saved`
          : `Reprocessed with status "${r?.status}": ${r?.recordsSaved ?? 0} records saved${r?.errors?.length ? ` — ${r.errors[0]}` : ''}`,
        r?.status === 'success' ? 'success' : 'warning',
      );
    },
    onError: (error: Error) => {
      showToast(`Reprocess failed: ${error.message}`, 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-history'] });
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
    },
  });

  // Reprocess every failed/partial email (newest first, server-side sequential)
  const reprocessAllFailedMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/reprocess-failed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error?.message || result?.message || 'Reprocess failed');
      return result;
    },
    onSuccess: (data) => {
      const partial = data?.partial ?? 0;
      const failed = data?.failed ?? 0;
      showToast(
        `Reprocessed ${data?.attempted ?? 0} failed emails: ${data?.succeeded ?? 0} succeeded, ${partial} partial, ${failed} still failing` +
          (partial > 0 ? ` (${partial} partial still need attention)` : ''),
        failed === 0 && partial === 0 ? 'success' : 'warning',
      );
    },
    onError: (error: Error) => {
      showToast(`Reprocess all failed: ${error.message}`, 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-history'] });
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
    },
  });

  // Roll back everything an email ingestion wrote and forget the email so the
  // next inbox poll re-ingests it (for re-running after a parser fix).
  const rollbackEmailMutation = useMutation({
    mutationFn: async (ingestionId: string) => {
      const response = await fetch(`${getApiUrl()}/email-map/admin/rollback/${ingestionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error?.message || result?.message || 'Rollback failed');
      return result;
    },
    onSuccess: (data) => {
      showToast(
        `Rolled back ${data.pricingDeleted || 0} pricing records` +
          (data.pricingReverted ? ` (reverted ${data.pricingReverted})` : '') +
          '. The email will be re-ingested on the next inbox check — use "Check Now" to run it immediately.',
        'success',
      );
      setEmailDetailsOpen(false);
      setSelectedEmail(null);
    },
    onError: (error: Error) => {
      showToast(`Rollback failed: ${error.message}`, 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-history'] });
      queryClient.invalidateQueries({ queryKey: ['email-map-admin-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-submissions'] });
    },
  });

  const handleCopyEmailAddress = () => {
    if (emailAddressData?.forwardingAddress) {
      navigator.clipboard.writeText(emailAddressData.forwardingAddress);
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2000);
    }
  };

  // Dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadResult(null);
      setUploadError(null);
      uploadMutation.mutate(acceptedFiles[0]);
    }
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'processed':
        return <StatusChip label="Processed" tone="positive" size="small" icon={<Done />} />;
      case 'pending':
        return <StatusChip label="Pending" tone="caution" size="small" icon={<HourglassEmpty />} />;
      case 'approved':
        return <StatusChip label="Approved" tone="info" size="small" icon={<CheckCircle />} />;
      case 'rejected':
        return <StatusChip label="Rejected" tone="problem" size="small" icon={<Close />} />;
      case 'duplicate':
        return <StatusChip label="Duplicate" tone="caution" size="small" icon={<Description />} />;
      default:
        return <StatusChip label={statusLabel(status)} tone={statusTone(status)} size="small" />;
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop();
    switch (ext) {
      case 'xlsx':
      case 'xls':
        return <InsertDriveFile color="success" />;
      case 'csv':
        return <Description color="primary" />;
      case 'pdf':
        return <FilePresent color="error" />;
      default:
        return <InsertDriveFile />;
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Pricing Ingestion"
        description="Upload and manage MAP pricing and rebate data from vendors"
      />

      {/* Stats Cards */}
      <Box sx={{ mb: 3 }}>
        <KpiGrid minWidth={200}>
          <KpiCard
            label="Catalog pricing records"
            loading={statsLoading}
            value={formatNumber(stats?.pricing.total ?? 0)}
            note={`${formatNumber(stats?.pricing.active ?? 0)} active`}
          />
          <KpiCard
            label="Files processed"
            loading={statsLoading}
            value={formatNumber(stats?.submissions.processed ?? 0)}
            note={
              <>
                <Box component="span" sx={{ display: 'block', color: tokens.signal.caution.ink }}>
                  {formatNumber(pendingApprovalCount)} awaiting approval
                </Box>
                {(stats?.submissions.pending ?? 0) > 0 && (
                  <Box component="span" sx={{ display: 'block' }}>
                    {formatNumber(stats?.submissions.pending ?? 0)} awaiting store decision
                  </Box>
                )}
              </>
            }
          />
          <KpiCard
            label="Rows written by uploads"
            loading={statsLoading}
            value={formatNumber(stats?.records.created ?? 0)}
            note={`${formatNumber(stats?.records.updated ?? 0)} updated · all time, includes store-only rows and rows since replaced`}
          />
          <KpiCard
            label="Product placeholders"
            loading={statsLoading}
            value={formatNumber(stats?.products.placeholder ?? 0)}
            note="awaiting full data"
          />
        </KpiGrid>
      </Box>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={(_, v) => setSelectedTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Upload" value="upload" />
        <Tab label={`Pending Approval (${formatNumber(pendingApprovalCount)})`} value="pending" />
        <Tab label="Submission History" value="history" />
        <Tab label="Email Ingestion" icon={<Email sx={{ fontSize: 16 }} />} iconPosition="end" value="email" />
      </Tabs>

      {/* Upload Tab */}
      {selectedTab === 'upload' && (
        <Box>
          {/* Dropzone */}
          <Card
            sx={{
              mb: 3,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: 'action.hover',
              },
            }}
            {...getRootProps()}
          >
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <input {...getInputProps()} />
              {uploadMutation.isPending ? (
                <>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="h6">Processing file...</Typography>
                  <LinearProgress sx={{ mt: 2, maxWidth: 300, mx: 'auto' }} />
                </>
              ) : (
                <>
                  <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Drop the file here' : 'Drag & drop a pricing file'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Supports: Excel (.xlsx, .xlsm, .xls), CSV, PDF • Max 50MB
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* Upload Result */}
          {uploadError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}

          {uploadResult && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {uploadResult.errors.length === 0 ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="h6">
                    {uploadResult.errors.length === 0 ? 'Upload Successful' : 'Upload Completed with Errors'}
                  </Typography>
                </Box>

                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>File:</strong> {uploadResult.fileName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Vendor:</strong> {uploadResult.vendor} • <strong>Region:</strong> {uploadResult.region}
                    {uploadResult.category && <> • <strong>Category:</strong> {uploadResult.category}</>}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {getStatusChip(uploadResult.status)}
                  </Typography>
                </Stack>

                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <Box>
                    <Typography variant="h4" color="primary">{uploadResult.recordsFound}</Typography>
                    <Typography variant="caption">Records Found</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="success.main">{uploadResult.recordsCreated}</Typography>
                    <Typography variant="caption">Created</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="info.main">{uploadResult.recordsUpdated}</Typography>
                    <Typography variant="caption">Updated</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="warning.main">{uploadResult.placeholdersCreated}</Typography>
                    <Typography variant="caption">Placeholders</Typography>
                  </Box>
                </Box>

                {uploadWarnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Warnings:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {uploadWarnings.slice(0, 5).map((w, i) => (
                        <li key={i}><Typography variant="caption">{w}</Typography></li>
                      ))}
                      {uploadWarnings.length > 5 && (
                        <li><Typography variant="caption">...and {uploadWarnings.length - 5} more</Typography></li>
                      )}
                    </ul>
                  </Alert>
                )}

                {uploadResult.errors.length > 0 && (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight="bold">Errors:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {uploadResult.errors.map((e, i) => (
                        <li key={i}><Typography variant="caption">{e}</Typography></li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recent submissions: newest first, bounded to the last 8 */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Recent submissions</Typography>
                <Button size="small" onClick={() => setSelectedTab('history')}>
                  View all
                </Button>
              </Box>
              {submissionsLoading ? (
                <TableSkeleton rows={4} columns={4} />
              ) : !submissions || submissions.length === 0 ? (
                <EmptyState size="compact" icon={<Description />} title="No submissions yet" />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Rows</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...submissions]
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .slice(0, 8)
                        .map((sub) => {
                          const arrived = arrivalCaption(sub);
                          return (
                          <TableRow
                            key={sub.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedSubmission(sub);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography variant="body2" noWrap title={sub.fileName}>{sub.fileName}</Typography>
                              {arrived && (
                                <Typography variant="caption" color="text.secondary" noWrap title={arrived} display="block">
                                  {arrived}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{sub.vendor}</TableCell>
                            <TableCell>{getStatusChip(sub.status)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatNumber(sub.recordsCreated + sub.recordsUpdated)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatDate(sub.createdAt, 'en-US', { hour: 'numeric', minute: '2-digit' })}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <CorrectRebateDatesDialog
        submission={correctingSubmission}
        approveAction={correctingSubmission?.privateToOrganization ? null : 'approve'}
        invalidateKeys={['ingestion-pending', 'ingestion-submissions', 'ingestion-stats']}
        onClose={() => setCorrectingSubmission(null)}
      />

      {/* Pending Approval Tab */}
      {selectedTab === 'pending' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Pending Submissions</Typography>
              <Button startIcon={<Refresh />} onClick={() => {
                refetchPending();
                queryClient.invalidateQueries({ queryKey: ['ingestion-stats'] });
              }}>
                Refresh
              </Button>
            </Box>

            {!pendingSubmissions || pendingSubmissions.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">No pending submissions</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell>Records</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingSubmissions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getFileIcon(sub.fileName)}
                            <Typography variant="body2">{sub.fileName}</Typography>
                          </Box>
                          {sub.windowHold?.status === 'open' && (
                            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
                              {sub.windowHold.message}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{sub.vendor}</TableCell>
                        <TableCell><Chip label={sub.region} size="small" /></TableCell>
                        <TableCell>{sub.recordsFound}</TableCell>
                        <TableCell>
                          {formatSubmittedAt(sub.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {sub.windowHold?.status === 'open' && sub.windowHold.correctable && (
                              <Button size="small" variant="outlined" onClick={() => setCorrectingSubmission(sub)}>
                                Correct dates
                              </Button>
                            )}
                            {!sub.privateToOrganization && (sub.windowHold?.status !== 'open' || sub.windowHold.approvableAsIs) && (
                              <Tooltip title={sub.windowHold?.status === 'open' ? 'Approve with the dates as they are' : 'Approve'}>
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => approveMutation.mutate(sub.id)}
                                  disabled={approveMutation.isPending}
                                >
                                  <CheckCircle />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) {
                                    rejectMutation.mutate({ submissionId: sub.id, reason });
                                  }
                                }}
                                disabled={rejectMutation.isPending}
                              >
                                <Close />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {selectedTab === 'history' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Submission History</Typography>
              <Button startIcon={<Refresh />} onClick={() => refetchSubmissions()}>
                Refresh
              </Button>
            </Box>

            {submissionsLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} />
                ))}
              </Stack>
            ) : !submissions || submissions.length === 0 ? (
              <EmptyState size="compact" icon={<Description />} title="No submissions yet" />
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Records</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.map((sub) => (
                      <TableRow 
                        key={sub.id} 
                        hover 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedSubmission(sub);
                          setDetailsDialogOpen(true);
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getFileIcon(sub.fileName)}
                            <Box>
                              <Typography variant="body2">{sub.fileName}</Typography>
                              {arrivalCaption(sub) && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {arrivalCaption(sub)}
                                </Typography>
                              )}
                              {sub.category && (
                                <Typography variant="caption" color="text.secondary">
                                  {sub.category}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{sub.vendor}</TableCell>
                        <TableCell><Chip label={sub.region} size="small" /></TableCell>
                        <TableCell>{getStatusChip(sub.status)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sub.recordsCreated} created, {sub.recordsUpdated} updated
                          </Typography>
                          {sub.errors.length > 0 && (
                            <Typography variant="caption" color="error">
                              {sub.errors.length} errors
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(sub.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(sub.createdAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Ingestion Tab */}
      {selectedTab === 'email' && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Email color="primary" />
                <Typography variant="h6" fontWeight={600}>Platform Email Ingestion</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vendors and distributors can forward MAP pricing or rebate files (Excel/CSV/PDF) to the
                address below. Emails are polled automatically every 5 minutes and applied platform-wide —
                the resulting pricing updates become visible to all stores through the product catalog.
              </Typography>

              {emailAddressLoading ? (
                <Skeleton variant="rounded" height={48} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ color: 'primary.main', flex: 1, userSelect: 'all', fontFamily: tokens.font.mono }}
                  >
                    {emailAddressData?.forwardingAddress || 'Loading...'}
                  </Typography>
                  <Tooltip title={addressCopied ? 'Copied!' : 'Copy address'}>
                    <IconButton size="small" onClick={handleCopyEmailAddress}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={checkEmailNowMutation.isPending ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={() => checkEmailNowMutation.mutate()}
                  disabled={checkEmailNowMutation.isPending}
                >
                  {checkEmailNowMutation.isPending ? 'Checking...' : 'Check Now'}
                </Button>
                {checkEmailNowMutation.isSuccess && (
                  <Button
                    variant="text"
                    size="small"
                    color="success"
                    disabled={!checkEmailNowMutation.data?.results?.length}
                    onClick={() => {
                      const first = checkEmailNowMutation.data?.results?.[0] as EmailIngestionRecord | undefined;
                      if (first) openEmailDetails(first);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {checkEmailNowMutation.data?.emailsProcessed || 0} emails processed
                    {checkEmailNowMutation.data?.results?.length ? ' — view prices' : ''}
                  </Button>
                )}
                {checkEmailNowMutation.isError && (
                  <Typography variant="body2" color="error.main">
                    {(checkEmailNowMutation.error as Error)?.message || 'Check failed'}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Stats */}
          {emailStats && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
                <Card>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{formatNumber(emailStats.totalEmailsProcessed)}</Typography>
                    <Typography variant="caption" color="text.secondary">Emails Processed</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
                <Card>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{formatNumber(emailStats.totalRecordsSaved)}</Typography>
                    <Typography variant="caption" color="text.secondary">Records Saved</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
                <Card>
                  <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>{emailStats.successRate}</Typography>
                    <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {/* History */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Ingestion History (All Organizations)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={reprocessAllFailedMutation.isPending ? <CircularProgress size={16} /> : <Replay />}
                    disabled={reprocessAllFailedMutation.isPending}
                    onClick={() => setReprocessAllConfirmOpen(true)}
                  >
                    Reprocess all failed
                  </Button>
                  <Button startIcon={<Refresh />} onClick={() => refetchEmailHistory()}>
                    Refresh
                  </Button>
                </Box>
              </Box>

              {emailHistoryLoading ? (
                <Stack spacing={1}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={52} />
                  ))}
                </Stack>
              ) : !emailHistory || emailHistory.length === 0 ? (
                <EmptyState
                  size="compact"
                  icon={<Email />}
                  title="No emails processed yet"
                  description="Forward a pricing email to get started."
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell align="center">Attachments</TableCell>
                        <TableCell align="center">Records</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailHistory.map((record) => (
                        <TableRow
                          key={record.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openEmailDetails(record)}
                        >
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                              {record.from}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                              {record.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={record.organizationId ? 'Org' : 'Global'}
                              size="small"
                              variant="outlined"
                              color={record.organizationId ? 'default' : 'primary'}
                            />
                          </TableCell>
                          <TableCell align="center">{record.attachmentsProcessed}</TableCell>
                          <TableCell align="center">
                            {record.recordsSaved > 0
                              ? record.recordsSaved
                              : (record.recordsParsed || 0)}
                            {record.recordsSaved === 0 && record.recordsParsed > 0 ? (
                              <Typography component="span" variant="caption" color="text.secondary" display="block">
                                pending save
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                record.errors?.length ? (
                                  <Box sx={{ whiteSpace: 'pre-line' }}>{record.errors.join('\n')}</Box>
                                ) : record.notes?.length ? (
                                  <Box sx={{ whiteSpace: 'pre-line' }}>{record.notes.join('\n')}</Box>
                                ) : (
                                  'Click to view updated product prices'
                                )
                              }
                            >
                              <StatusChip
                                label={statusLabel(record.status)}
                                size="small"
                                tone={record.status === 'success' ? 'positive' : record.status === 'partial' ? 'caution' : 'problem'}
                              />
                            </Tooltip>
                            {record.errors?.[0] ? (
                              <Typography
                                variant="caption"
                                color="error.main"
                                sx={{ display: 'block', maxWidth: 280, mt: 0.5 }}
                              >
                                {record.errors[0]}
                              </Typography>
                            ) : null}
                            {record.parserFix ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', maxWidth: 280, mt: 0.5 }}
                              >
                                {record.parserFix.prUrl ? (
                                  <Link
                                    href={record.parserFix.prUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    Parser fix
                                  </Link>
                                ) : record.parserFix.status === 'queued' ? (
                                  'Parser fix queued'
                                ) : (
                                  'Parser fix needs a person'
                                )}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption">
                                {record.processedAt ? new Date(record.processedAt).toLocaleString() : '-'}
                              </Typography>
                              <Visibility fontSize="small" color="action" />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {record.status !== 'success' && (
                              <Tooltip
                                title={
                                  record.retryCount
                                    ? `Reprocess this email (retried ${record.retryCount}x)`
                                    : 'Reprocess this email'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={reprocessEmailMutation.isPending}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      reprocessEmailMutation.mutate(record.id);
                                    }}
                                  >
                                    {reprocessEmailMutation.isPending &&
                                    reprocessEmailMutation.variables === record.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <Replay fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Danger Zone */}
      <Card sx={{ mt: 4, border: 1, borderColor: 'error.main' }}>
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Danger Zone
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle2">Clear all pricing data</Typography>
              <Typography variant="body2" color="text.secondary">
                Permanently deletes all pricing records, submissions, and product placeholders. This cannot be undone.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => setClearAllConfirmOpen(true)}
              disabled={clearAllMutation.isPending}
              sx={{ flexShrink: 0 }}
            >
              {clearAllMutation.isPending ? 'Clearing...' : 'Clear All Data'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Email ingestion pricing details */}
      <Dialog
        open={emailDetailsOpen}
        onClose={() => {
          setEmailDetailsOpen(false);
          setSelectedEmail(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <Typography variant="h6">Email Pricing Updates</Typography>
          {selectedEmail && (
            <Typography variant="body2" color="text.secondary">
              {selectedEmail.subject || 'No subject'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmail && (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip label={`From: ${selectedEmail.from || '—'}`} size="small" variant="outlined" />
                <Chip
                  label={selectedEmail.organizationId ? 'Org scope' : 'Global'}
                  size="small"
                  color={selectedEmail.organizationId ? 'default' : 'primary'}
                  variant="outlined"
                />
                <StatusChip
                  label={statusLabel(selectedEmail.status)}
                  size="small"
                  tone={
                    selectedEmail.status === 'success'
                      ? 'positive'
                      : selectedEmail.status === 'partial'
                        ? 'caution'
                        : 'problem'
                  }
                />
                <Chip
                  label={`${selectedEmail.recordsSaved || selectedEmail.recordsParsed || 0} records`}
                  size="small"
                  variant="outlined"
                />
                {selectedEmail.processedAt && (
                  <Chip
                    label={new Date(selectedEmail.processedAt).toLocaleString()}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              {selectedEmail.errors?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {selectedEmail.errors.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </Alert>
              )}

              {(selectedEmail.notes?.length ?? 0) > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedEmail.notes!.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </Alert>
              )}

              {selectedEmail.discontinuedReport ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Discontinued products report
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      label={`${selectedEmail.discontinuedReport.rowsProcessed} rows in report`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${selectedEmail.discontinuedReport.productsUpdated} newly discontinued`}
                      size="small"
                      color={selectedEmail.discontinuedReport.productsUpdated > 0 ? 'warning' : 'default'}
                    />
                    <Chip
                      label={`${selectedEmail.discontinuedReport.alreadyDiscontinued} already discontinued`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${selectedEmail.discontinuedReport.unmatched} not in catalog`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ProMaster&apos;s report covers every Daymen brand (Delkin, Gitzo, Manfrotto, SmallRig, ...).
                    Rows for products we don&apos;t carry as ProMaster catalog items are expected and counted
                    as &quot;not in catalog&quot;.
                  </Alert>
                  {(selectedEmail.discontinuedReport.unmatchedSamples?.length ?? 0) > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Sample of rows not in catalog
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                        <Table size="small" stickyHeader>
                          <TableBody>
                            {selectedEmail.discontinuedReport.unmatchedSamples!.map((sample) => (
                              <TableRow key={sample}>
                                <TableCell>
                                  <Typography variant="body2">{sample}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              ) : !selectedEmail.submissionIds?.length ? (
                <Alert severity="info">
                  No linked pricing submission for this email. Older ingestions may not have detail available.
                </Alert>
              ) : emailRecordsLoading ? (
                <LoadingState />
              ) : emailPricingRecords && emailPricingRecords.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Updated product prices ({emailPricingRecords.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Model</TableCell>
                          <TableCell>Brand</TableCell>
                          <TableCell>Region</TableCell>
                          <TableCell align="right">MAP Price</TableCell>
                          <TableCell align="right">Rebate</TableCell>
                          <TableCell>Effective</TableCell>
                          <TableCell>Expires</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {emailPricingRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{record.model}</Typography>
                            </TableCell>
                            <TableCell>{record.brand}</TableCell>
                            <TableCell><Chip label={record.region} size="small" /></TableCell>
                            <TableCell align="right">
                              {record.mapPrice != null ? `$${Number(record.mapPrice).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {record.instantRebate != null ? `$${Number(record.instantRebate).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell>
                              {record.effectiveDate ? new Date(record.effectiveDate).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              {record.endDate ? new Date(record.endDate).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <StatusChip
                                label={statusLabel(record.status)}
                                size="small"
                                tone={record.status === 'active' || record.status === 'approved' ? 'positive' : 'neutral'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">
                  No pricing rows found for this email&apos;s submission. Records may have been cleared.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedEmail?.submissionIds && selectedEmail.submissionIds.length > 0 && (
            <Tooltip title="Delete everything this email ingested and re-ingest it from the original attachment (e.g. after a parser fix)">
              <Button
                color="warning"
                startIcon={<Replay />}
                disabled={rollbackEmailMutation.isPending}
                onClick={() => setRollbackConfirmOpen(true)}
                sx={{ mr: 'auto' }}
              >
                Rollback & Re-ingest
              </Button>
            </Tooltip>
          )}
          <Button
            onClick={() => {
              setEmailDetailsOpen(false);
              setSelectedEmail(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedSubmission(null);
          setShowWarnings(false);
          setShowErrors(false);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Import Details</Typography>
              {selectedSubmission && (
                <Typography variant="body2" color="text.secondary">
                  {selectedSubmission.fileName}
                </Typography>
              )}
            </Box>
            {selectedSubmission && getStatusChip(selectedSubmission.status)}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSubmission && (
            <Box>
              <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">How it arrived</Typography>
                  <Typography variant="body1">
                    {selectedSubmission.source === 'email'
                      ? 'Email'
                      : selectedSubmission.source === 'upload' || selectedSubmission.source === 'admin'
                        ? 'Upload'
                        : 'Not recorded'}
                  </Typography>
                </Box>
                {(selectedSubmission.emailOriginalFrom || selectedSubmission.emailFrom || selectedSubmission.submitterEmail) && (
                  <Box sx={{ minWidth: 0, maxWidth: 420 }}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedSubmission.source === 'email' ? 'From' : 'Uploaded by'}
                    </Typography>
                    <Typography variant="body1">
                      {selectedSubmission.source === 'email'
                        ? (selectedSubmission.emailOriginalFrom || selectedSubmission.emailFrom)
                        : (selectedSubmission.submitterEmail || selectedSubmission.emailFrom)}
                    </Typography>
                  </Box>
                )}
                {selectedSubmission.source === 'email' && selectedSubmission.emailOriginalFrom && selectedSubmission.emailFrom && (
                  <Box sx={{ minWidth: 0, maxWidth: 420 }}>
                    <Typography variant="caption" color="text.secondary">Received as</Typography>
                    <Typography variant="body1">{selectedSubmission.emailFrom}</Typography>
                  </Box>
                )}
                {selectedSubmission.emailSubject && (
                  <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                    <Typography variant="caption" color="text.secondary">Subject</Typography>
                    <Typography variant="body1">{selectedSubmission.emailSubject}</Typography>
                  </Box>
                )}
              </Box>

              {/* Summary Stats */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Vendor</Typography>
                  <Typography variant="body1">{selectedSubmission.vendor}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Region</Typography>
                  <Typography variant="body1">{selectedSubmission.region}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1">{selectedSubmission.category || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Records Found</Typography>
                  <Typography variant="body1">{selectedSubmission.recordsFound}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body1" color="success.main">{selectedSubmission.recordsCreated}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Updated</Typography>
                  <Typography variant="body1" color="info.main">{selectedSubmission.recordsUpdated}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Placeholders</Typography>
                  <Typography variant="body1">{selectedSubmission.placeholdersCreated}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Imported</Typography>
                  <Typography variant="body1">
                    {new Date(selectedSubmission.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Warnings */}
              {detailWarnings.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    startIcon={showWarnings ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setShowWarnings(!showWarnings)}
                    color="warning"
                    size="small"
                  >
                    {detailWarnings.length} Warnings
                  </Button>
                  <Collapse in={showWarnings}>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      <List dense>
                        {detailWarnings.map((warning, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText primary={warning} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  </Collapse>
                </Box>
              )}

              {/* Errors */}
              {selectedSubmission.errors && selectedSubmission.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    startIcon={showErrors ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setShowErrors(!showErrors)}
                    color="error"
                    size="small"
                  >
                    {selectedSubmission.errors.length} Errors
                  </Button>
                  <Collapse in={showErrors}>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <List dense>
                        {selectedSubmission.errors.map((error, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText primary={error} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  </Collapse>
                </Box>
              )}

              {/* Imported Records Table */}
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Imported Pricing Records
              </Typography>
              {recordsLoading ? (
                <LoadingState />
              ) : submissionRecords && submissionRecords.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell>Brand</TableCell>
                        <TableCell>Region</TableCell>
                        <TableCell align="right">MAP Price</TableCell>
                        <TableCell align="right">Rebate</TableCell>
                        <TableCell>Effective</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {submissionRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{record.model}</Typography>
                          </TableCell>
                          <TableCell>{record.brand}</TableCell>
                          <TableCell><Chip label={record.region} size="small" /></TableCell>
                          <TableCell align="right">
                            {record.mapPrice ? `$${record.mapPrice.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {record.instantRebate ? `$${record.instantRebate.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell>
                            {record.effectiveDate ? new Date(record.effectiveDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {record.endDate ? new Date(record.endDate).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={statusLabel(record.status)}
                              size="small"
                              tone={record.status === 'active' ? 'positive' : record.status === 'upcoming' ? 'info' : 'neutral'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No pricing records found for this submission. Records may have been cleared or the submission is still pending.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailsDialogOpen(false);
            setSelectedSubmission(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={rollbackConfirmOpen}
        title="Rollback & Re-ingest Email"
        message={
          `This will delete the ${selectedEmail?.recordsSaved || 0} pricing records saved from ` +
          `"${selectedEmail?.subject || 'this email'}" (restoring any previously overwritten values) and ` +
          'queue the email for re-ingestion on the next inbox check. Continue?'
        }
        confirmLabel="Rollback & Re-ingest"
        confirmColor="warning"
        loading={rollbackEmailMutation.isPending}
        onConfirm={() => {
          if (selectedEmail) {
            rollbackEmailMutation.mutate(selectedEmail.id, {
              onSettled: () => setRollbackConfirmOpen(false),
            });
          }
        }}
        onClose={() => setRollbackConfirmOpen(false)}
      />

      <ConfirmDialog
        open={reprocessAllConfirmOpen}
        title="Reprocess all failed emails"
        message="This refetches up to 20 of the newest failed or partial emails from Gmail, one at a time, and runs the current parsers on each. Attachments already saved are skipped. Continue?"
        confirmLabel="Reprocess all"
        loading={reprocessAllFailedMutation.isPending}
        onConfirm={() => {
          reprocessAllFailedMutation.mutate(undefined, { onSettled: () => setReprocessAllConfirmOpen(false) });
        }}
        onClose={() => setReprocessAllConfirmOpen(false)}
      />

      <ConfirmDialog
        open={clearAllConfirmOpen}
        title="Clear All Pricing Data"
        message="Are you sure you want to clear ALL pricing data? This will delete all pricing records, submissions, and product placeholders. This cannot be undone."
        confirmLabel="Clear All"
        confirmColor="error"
        requireText="CLEAR ALL DATA"
        loading={clearAllMutation.isPending}
        onConfirm={() => {
          clearAllMutation.mutate(undefined, { onSettled: () => setClearAllConfirmOpen(false) });
        }}
        onClose={() => setClearAllConfirmOpen(false)}
      />
    </PageContainer>
  );
};

export default AdminPricingIngestionPage;
